import { ScanType } from '../../types';
import { ScanTool } from './ToolSelector';

/**
 * The scan types offered by every form that starts a scan — the New Scan modal and
 * the schedule form alike. One list, because a scheduled scan runs through exactly the
 * same scanner as an ad-hoc one: anything the two forms disagree about is a lie in one
 * of them.
 *
 * The schedule form used to keep its own copy, and it drifted. It was missing
 * Performance entirely, and it still described `toxicLinks` as the inbound-backlink
 * check gated on Search Console — a different feature that lives behind an AppSync
 * resolver and is not driven by a scan type at all. Picking it in a schedule ran the
 * outbound check, the same as everywhere else, while the Search Console gate hid the
 * option from every site without a connection.
 */

// Which engine each scan type runs, shown as a read-only badge. Tools follow the
// scan type, they are no longer chosen by hand.
export type ToolBadge = 'axe' | 'lighthouse' | 'internal';

export const BASE_SCAN_TYPES: {
  value: ScanType;
  label: string;
  description: string;
  tools: ToolBadge[];
}[] = [
  {
    value: 'accessibility',
    label: 'Accessibility',
    description: 'WCAG violations and best practices',
    tools: ['axe', 'lighthouse'],
  },
  {
    value: 'eaa',
    label: 'EAA Compliance',
    description: 'EN 301 549 / European Accessibility Act',
    tools: ['axe', 'internal'],
  },
  {
    value: 'seo',
    label: 'SEO',
    description: 'Search engine optimization issues',
    tools: ['lighthouse', 'internal'],
  },
  {
    value: 'geo',
    label: 'GEO',
    description: 'AI search engine optimization (Google SGE, Bing Chat, Perplexity)',
    tools: ['internal'],
  },
  {
    value: 'performance',
    label: 'Performance',
    description: 'Core Web Vitals and speed metrics',
    tools: ['lighthouse'],
  },
  {
    value: 'brokenLinks',
    label: 'Broken Links',
    description: 'Internal and external link checking',
    tools: ['internal'],
  },
  // No Search Console gate: the outbound check reads the page's own HTML and probes
  // the links it finds.
  {
    value: 'toxicLinks',
    label: 'Toxic Links (Outbound)',
    description: 'Check outbound links for toxic or spammy destinations',
    tools: ['internal'],
  },
];

export const TOOL_BADGE: Record<ToolBadge, { label: string; className: string }> = {
  axe: { label: 'axe', className: 'text-white bg-[#6366f1]' },
  lighthouse: { label: 'Lighthouse', className: 'text-white bg-[#f97316]' },
  internal: { label: 'internal', className: 'text-white bg-[#10b981]' },
};

/**
 * Derive the real engines from the selected scan types. Must stay in sync with the
 * scanner: axe runs for Accessibility/EAA, Lighthouse for Accessibility (Full mode
 * only) and Performance. In Quick mode the Accessibility scan is axe-only (skips the
 * slower Lighthouse render); Performance still uses Lighthouse (PageSpeed) either way.
 */
export const deriveSelectedTools = (
  types: ScanType[],
  accessibilityMode: 'quick' | 'full'
): ScanTool[] => {
  const tools: ScanTool[] = [];
  if (types.includes('accessibility') || types.includes('eaa')) tools.push('axe');
  const lighthouseForA11y = types.includes('accessibility') && accessibilityMode === 'full';
  if (lighthouseForA11y || types.includes('performance')) tools.push('lighthouse');
  return tools;
};
